import { parse } from 'csv-parse/browser/esm/sync';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FiUpload } from 'react-icons/fi';

import TopModalSection from '../common/TopModalSection';

import { formatPrice } from '@/components/utils/utils';
import { useUserStore } from '@/hooks/useUserStore';
import { ICreateProductPayload } from '@/interfaces/api/product/ICreateProductPayload';
import {
	IProduct,
	IProductVariant,
} from '@/interfaces/api/product/IProductResponse';
import { haggleApiService } from '@/services/haggle.service';
import { mapCsvToProducts } from '@/utils/csvProductParser';

interface CsvUploadModalProps {
	closeModal: () => void;
}

const CsvUploadModal = ({ closeModal }: CsvUploadModalProps) => {
	const [products, setProducts] = useState<IProduct[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const { store } = useUserStore();

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		accept: {
			'text/csv': ['.csv'],
		},
		maxFiles: 1,
		onDrop: async (acceptedFiles: File[]) => {
			setError(null);
			if (acceptedFiles.length === 0) return;
			try {
				const file = acceptedFiles[0];
				const text = await file.text();
				const records = parse(text, {
					columns: true,
					skip_empty_lines: true,
					trim: true,
				});
				const mappedProducts = mapCsvToProducts(records);
				setProducts(mappedProducts);
			} catch (err) {
				setError('Failed to parse CSV. Please check your file format.');
			}
		},
	});

	// Flatten products/variants for table
	const flattenedRows: { product: IProduct; variant: IProductVariant }[] =
		products.flatMap((product) =>
			product.variants.length > 0
				? product.variants.map((variant) => ({ product, variant }))
				: [
						{
							product,
							variant: {
								id: 0,
								userId: '',
								handle: product.handle,
								type: '',
								name: '',
								sku: '',
								grams: 0,
								inventoryQty: 0,
								price: '',
								requiresShipping: false,
								barcode: '',
								image: '',
								weightUnit: '',
								productInfo: '',
								createdAt: '',
								updatedAt: '',
								deletedAt: '',
								uuid: '',
							},
						},
				  ],
		);

	// Upload in batches of 10 using the real API
	const handleUpload = async () => {
		setIsUploading(true);
		setUploadProgress(0);
		setError(null);
		const total = products.length;
		const batchSize = 10;
		let uploaded = 0;
		try {
			for (let i = 0; i < total; i += batchSize) {
				const batch: ICreateProductPayload[] = products
					.slice(i, i + batchSize)
					.map((p) => ({
						handle: `${store?.tradeName ?? p.vendor}-${p.handle}`,
						title: p.title,
						bodyHtml: p.bodyHtml,
						productInfo: p.productInfo,
						type: p.type,
						tags: p.tags,
						published: p.published,
						imageSrc: p.imageSrc,
						variants: p.variants.map((v) => ({
							handle: `${store?.tradeName ?? p.vendor}-${v.handle}`,
							type: v.type,
							name: v.name,
							sku: v.sku,
							grams: v.grams,
							inventoryQty: v.inventoryQty,
							price:
								typeof v.price === 'string' ? parseFloat(v.price) : v.price,
							requiresShipping: v.requiresShipping,
							barcode: v.barcode,
							image: v.image,
							weightUnit: v.weightUnit,
						})),
					}));
				await haggleApiService.bulkCreateProducts(batch);
				uploaded += batch.length;
				setUploadProgress(Math.round((uploaded / total) * 100));
			}
			setTimeout(() => {
				setIsUploading(false);
				setUploadProgress(100);
				closeModal();
			}, 500);
		} catch (err) {
			setIsUploading(false);
			setError('An error occurred while uploading products. Please try again.');
		}
	};

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
			<div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
				<TopModalSection title="Upload Products via CSV" close={closeModal} />
				<div className="p-6">
					{/* Dropzone - only show if no products and not uploading */}
					{products.length === 0 && !isUploading && (
						<div
							{...getRootProps()}
							className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
								${
									isDragActive
										? 'border-purpleBrand-500 bg-purpleBrand-50'
										: 'border-gray-300 hover:border-purpleBrand-500'
								}`}
						>
							<input {...getInputProps()} disabled={isUploading} />
							<FiUpload className="mx-auto h-12 w-12 text-gray-400" />
							<p className="mt-2 text-sm text-gray-600">
								{isDragActive
									? 'Drop the CSV file here'
									: 'Drag and drop a CSV file here, or click to select'}
							</p>
							<p className="mt-1 text-xs text-gray-500">
								Only CSV files are supported
							</p>
						</div>
					)}

					{error && <div className="mt-4 text-red-600 text-sm">{error}</div>}

					{/* Upload Progress Bar */}
					{isUploading && (
						<div className="mt-6 w-full">
							<div className="flex items-center justify-between mb-1">
								<span className="text-sm font-medium text-purpleBrand-700">
									Uploading...
								</span>
								<span className="text-xs font-medium text-purpleBrand-700">
									{uploadProgress}%
								</span>
							</div>
							<div className="w-full bg-gray-200 rounded-full h-3">
								<div
									className="bg-purpleBrand-500 h-3 rounded-full transition-all duration-300"
									style={{ width: `${uploadProgress}%` }}
								></div>
							</div>
							<div className="mt-2 text-sm text-gray-600 text-center">
								Please do not close this modal until the upload is complete.
							</div>
						</div>
					)}

					{/* Preview Table */}
					{flattenedRows.length > 0 && (
						<div className="mt-6">
							<h3 className="text-lg font-medium mb-4">Preview</h3>
							<div className="overflow-x-auto max-h-[400px] overflow-y-auto border rounded-lg">
								<table className="min-w-full divide-y divide-gray-200">
									<thead className="bg-gray-50">
										<tr>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Title
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Type
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Tags
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Variant Name
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												SKU
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Price
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Inventory Qty
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Barcode
											</th>
										</tr>
									</thead>
									<tbody className="bg-white divide-y divide-gray-200">
										{flattenedRows.map(({ product, variant }, idx) => (
											<tr key={product.id + '-' + (variant?.sku || idx)}>
												<td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
													{product.title}
												</td>
												<td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
													{product.type}
												</td>
												<td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
													{product.tags}
												</td>
												<td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
													{variant?.name || '—'}
												</td>
												<td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
													{variant?.sku || '—'}
												</td>
												<td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
													{formatPrice(variant?.price || '—')}
												</td>
												<td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
													{variant?.inventoryQty ?? '—'}
												</td>
												<td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
													{variant?.barcode || '—'}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					)}

					{/* Action Buttons */}
					<div className="mt-6 flex justify-end gap-3 sticky bottom-0 bg-white pt-4 z-10">
						<button
							onClick={closeModal}
							className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
							disabled={isUploading}
						>
							Cancel
						</button>
						<button
							onClick={handleUpload}
							disabled={products.length === 0 || isUploading}
							className="px-4 py-2 text-sm text-white rounded-lg bg-purpleBrand-500 hover:bg-purpleBrand-600 active:bg-purpleBrand-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{isUploading ? 'Uploading...' : 'Upload Products'}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CsvUploadModal;
